import React, { useEffect, useRef } from 'react';
import { UI_TEXT } from '../../config/ui-text';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (token: string) => void; 'error-callback'?: () => void },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(script);
  });
}

interface TurnstileChallengeProps {
  /** Called with the Turnstile token once the challenge is solved. */
  onVerified: (token: string) => void;
  /** Called if the widget can't render or errors (so the host can decide a fallback). */
  onError?: () => void;
}

/**
 * Renders a Cloudflare Turnstile widget for borderline respondents. The site key
 * comes from VITE_TURNSTILE_SITE_KEY; if it is absent the component reports an
 * error so the caller can fall back gracefully rather than render a broken widget.
 */
export const TurnstileChallenge: React.FC<TurnstileChallengeProps> = ({ onVerified, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const copy = UI_TEXT.HUMAN_SURVEY.FRAUD_GATE.CHALLENGE;

  // Keep the latest callbacks in refs so the widget renders exactly once and is
  // not torn down/re-rendered when the parent re-renders with new lambda props.
  const onVerifiedRef = useRef(onVerified);
  const onErrorRef = useRef(onError);
  onVerifiedRef.current = onVerified;
  onErrorRef.current = onError;

  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
    if (!siteKey) {
      onErrorRef.current?.();
      return;
    }

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            if (!cancelled) onVerifiedRef.current(token);
          },
          'error-callback': () => onErrorRef.current?.(),
        });
      })
      .catch(() => onErrorRef.current?.());

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{copy.TITLE}</h2>
        <p className="text-gray-600 mb-6 leading-relaxed">{copy.MESSAGE}</p>
        <div ref={containerRef} className="flex justify-center" />
      </div>
    </div>
  );
};
